<template>
  <div
    :class="[
      'text-input-wrapper',
      classes,
      { focused, filled: hasValue, 'has-label': !!label },
    ]"
  >
    <div class="text-input-shell">
      <input
        :id="inputId"
        :type="type"
        class="form-control custom-text-field"
        :class="{ 'is-invalid': hasError, 'input-error': hasError }"
        :value="inputValue"
        :placeholder="label ? ' ' : placeholder"
        :disabled="disabled"
        :aria-invalid="hasError ? 'true' : 'false'"
        @input="updateValue($event.target.value)"
        @focus="onFocus"
        @blur="onBlur"
      />
      <label v-if="label" :for="inputId" class="floating-label">
        {{ label }}
      </label>
      <slot name="trailing" />
    </div>
    <div v-if="hasError && errMsg" class="invalid-feedback d-block">
      {{ errMsg }}
    </div>
  </div>
</template>

<script>
export default {
  name: "text-input",
  props: {
    label: String,
    type: {
      type: String,
      default: "text",
    },
    modelValue: {
      type: [String, Number],
      description: "Input value",
    },
    value: {
      type: [String, Number],
      description: "Legacy input value",
    },
    classes: {
      type: String,
      description: "String classes to be applied to the root element",
      default() {
        return ""
      },
    },
    hasError: {
      default: false,
    },
    errMsg: {
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "input", "focus", "blur"],
  data() {
    return {
      focused: false,
    }
  },
  computed: {
    inputValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    },
    hasValue() {
      return (
        this.inputValue !== undefined &&
        this.inputValue !== null &&
        `${this.inputValue}` !== ""
      )
    },
    inputId() {
      return `text-input-${this.$.uid}`
    },
  },
  methods: {
    updateValue(value) {
      this.$emit("update:modelValue", value)
      this.$emit("input", value)
    },
    onFocus(value) {
      this.focused = true
      this.$emit("focus", value)
    },
    onBlur(value) {
      this.focused = false
      this.$emit("blur", value)
    },
  },
}
</script>

<style scoped lang="scss">
.text-input-wrapper {
  width: 100%;
  margin-bottom: 24px;
  padding: 12px 0;
}

.text-input-shell {
  position: relative;
}

.custom-text-field {
  min-height: 48px;
  border-radius: 8px;
  border: 1px solid var(--Gray-300, #b0b9c9);
  background: var(--White, #fff);
  color: var(--Gray-900, #343946);
  padding: 10px 16px;
  cursor: text;
}

.custom-text-field:hover {
  border: 2px solid var(--Gray-500, #677690);
}

.custom-text-field:focus {
  border: 2px solid var(--gfb-blue-brand-500, #0095e2);
  box-shadow: none;
  outline: none;
}

.custom-text-field:disabled {
  border: 1px solid var(--Gray-200, #d5d9e2);
  background: var(--Gray-100, #eceef2);
  color: var(--Gray-300, #b0b9c9);
  cursor: not-allowed;
}

.custom-text-field:disabled + .floating-label {
  background: transparent;
}

.custom-text-field.input-error {
  border: 2px solid var(--Red-500, #e24646);
  box-shadow: none !important;
}

.custom-text-field.input-error + .floating-label {
  color: var(--Red-500, #e24646) !important;
}

.custom-text-field.input-error:hover {
  border: 2px solid var(--Red-500, #e24646);
}

.floating-label {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 4px;
  background: var(--White, #fff);
  color: var(--Gray-600, #525e77);
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  pointer-events: none;
  transition: top 0.15s ease, transform 0.15s ease, color 0.15s ease,
    font-size 0.15s ease;
}

.custom-text-field:focus + .floating-label {
  top: 0;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--gfb-blue-brand-500, #0095e2) !important;
  text-shadow: 0 0 0 var(--White, #fff);
}

.custom-text-field:not(:placeholder-shown) + .floating-label {
  top: 0;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--Gray-600, #525e77);
  text-shadow: 0 0 0 var(--White, #fff);
}

.text-input-wrapper .invalid-feedback {
  display: block;
  margin-top: 0.25rem;
}
</style>
